import { useState } from 'react';
import AppShell from '@/components/feature/AppShell';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/base/Toast';
import { teacherNotifications } from '@/mocks/dashboard';
import { students } from '@/mocks/students';
import {
  skillDomains,
  learningStyles,
  standardTLMMaterials,
} from '@/mocks/lessonPlan';
import StudentMultiSelect from '@/pages/dashboard/teacher-dashboard/lesson-plan/components/StudentMultiSelect';
import ProcedureStepRow, { type ProcedureStep } from '@/pages/dashboard/teacher-dashboard/lesson-plan/components/ProcedureStepRow';
import EvaluationRow, { type EvaluationMetric } from '@/pages/dashboard/teacher-dashboard/lesson-plan/components/EvaluationRow';

function genId(): string {
  return `id-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

const teacherStudents = students.filter(
  (s) => s.classTeacher === 'Mr. James Okafor' && s.status === 'enrolled'
);

export default function LessonPlanPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();

  // ── Form State ──
  const [planType, setPlanType] = useState<'individual' | 'group'>('group');
  const [targetStudentIds, setTargetStudentIds] = useState<string[]>([]);
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [theme, setTheme] = useState('');
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [longTermGoal, setLongTermGoal] = useState('');
  const [shortTermObjective, setShortTermObjective] = useState('');
  const [selectedStyles, setSelectedStyles] = useState<string[]>([]);
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([]);
  const [customMaterials, setCustomMaterials] = useState<string[]>([]);
  const [customMaterialInput, setCustomMaterialInput] = useState('');
  const [procedureSteps, setProcedureSteps] = useState<ProcedureStep[]>([]);
  const [evaluationMetrics, setEvaluationMetrics] = useState<EvaluationMetric[]>([]);
  const [followUpPlan, setFollowUpPlan] = useState('');
  const [finalEvaluation, setFinalEvaluation] = useState('');
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateNameInput, setTemplateNameInput] = useState('');

  // ── Handlers ──
  const toggleSkill = (id: string) => {
    setSelectedSkills((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleStyle = (id: string) => {
    setSelectedStyles((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleMaterial = (mat: string) => {
    setSelectedMaterials((prev) =>
      prev.includes(mat) ? prev.filter((x) => x !== mat) : [...prev, mat]
    );
  };

  const addCustomMaterial = () => {
    const trimmed = customMaterialInput.trim();
    if (trimmed && !customMaterials.includes(trimmed)) {
      setCustomMaterials((prev) => [...prev, trimmed]);
    }
    setCustomMaterialInput('');
  };

  const removeCustomMaterial = (mat: string) => {
    setCustomMaterials((prev) => prev.filter((x) => x !== mat));
  };

  const addStep = () => {
    setProcedureSteps((prev) => [
      ...prev,
      { id: genId(), stepTitle: '', educatorAction: '', studentExpectation: '', promptLevel: 'verbal' },
    ]);
  };

  const updateStep = (updated: ProcedureStep) => {
    setProcedureSteps((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
  };

  const removeStep = (id: string) => {
    setProcedureSteps((prev) => prev.filter((s) => s.id !== id));
  };

  const addMetric = () => {
    setEvaluationMetrics((prev) => [
      ...prev,
      { id: genId(), targetBehavior: '', measurementCriteria: 'frequency', successThreshold: '' },
    ]);
  };

  const updateMetric = (updated: EvaluationMetric) => {
    setEvaluationMetrics((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
  };

  const removeMetric = (id: string) => {
    setEvaluationMetrics((prev) => prev.filter((m) => m.id !== id));
  };

  // ── Validation ──
  const validate = (): string | null => {
    if (targetStudentIds.length === 0) return t('lessonPlans.validationSelectStudents');
    if (!dateStart || !dateEnd) return t('lessonPlans.validationDateRange');
    if (!theme.trim()) return t('lessonPlans.validationTheme');
    if (selectedSkills.length === 0) return t('lessonPlans.validationSkillDomain');
    if (!longTermGoal.trim()) return t('lessonPlans.validationLongTermGoal');
    if (!shortTermObjective.trim()) return t('lessonPlans.validationShortTermObjective');
    if (selectedStyles.length === 0) return t('lessonPlans.validationLearningStyles');
    if (procedureSteps.length === 0) return t('lessonPlans.validationProcedureSteps');
    return null;
  };

  const handleSaveDraft = () => {
    const err = validate();
    if (err) { toast.warning(err); return; }
    toast.success(t('lessonPlans.planSavedDraft'));
  };

  const handleSubmit = () => {
    const err = validate();
    if (err) { toast.warning(err); return; }
    toast.success(t('lessonPlans.planSubmitted'));
  };

  const handleSaveAsTemplate = () => {
    const trimmed = templateNameInput.trim();
    if (!trimmed) { toast.warning(t('lessonPlans.validationTheme')); return; }
    toast.success(t('lessonPlans.templateSaved'));
    setShowTemplateModal(false);
    setTemplateNameInput('');
  };

  return (
    <AppShell
      pageTitle={t('lessonPlans.pageTitle')}
      pageSubtitle={t('lessonPlans.pageSubtitle')}
      breadcrumbs={[
        { label: t('common.home') },
        { label: t('common.dashboard'), path: '/dashboard/teacher-dashboard' },
        { label: t('lessonPlans.listPageTitle'), path: '/dashboard/teacher-dashboard/lesson-plans' },
        { label: t('lessonPlans.pageTitle') },
      ]}
      notifications={teacherNotifications}
    >
      <div className="max-w-5xl mx-auto pb-12">
        {/* ── Plan Settings ── */}
        <section className="mb-8">
          <h3 className="font-heading font-700 text-lg text-foreground-900 mb-1">{t('lessonPlans.planSettings')}</h3>
          <p className="text-sm text-foreground-500 mb-5">{t('lessonPlans.planSettings')}</p>

          <div className="bg-background-50 border border-background-200 rounded-xl p-5 space-y-5">
            {/* Plan Type */}
            <div>
              <span className="text-sm font-label font-medium text-foreground-700 mb-2 block">{t('lessonPlans.planType')}</span>
              <div className="flex gap-3">
                {(['individual', 'group'] as const).map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => {
                      setPlanType(type);
                      setTargetStudentIds([]);
                    }}
                    className={`flex items-center gap-2.5 px-4 py-2.5 rounded-lg border-2 text-sm font-label font-medium transition-all cursor-pointer ${
                      planType === type
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-background-200 bg-background-50 text-foreground-600 hover:border-background-300'
                    }`}
                  >
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      planType === type ? 'border-primary-500' : 'border-background-300'
                    }`}>
                      {planType === type && <div className="w-2 h-2 rounded-full bg-primary-500" />}
                    </div>
                    {t(`lessonPlans.${type}`)}
                  </button>
                ))}
              </div>
            </div>

            {/* Target Students */}
            <div>
              <span className="text-sm font-label font-medium text-foreground-700 mb-2 block">{t('lessonPlans.targetStudents')}</span>
              <StudentMultiSelect
                students={teacherStudents}
                selectedIds={targetStudentIds}
                onChange={setTargetStudentIds}
                singleSelect={planType === 'individual'}
                placeholder={t('lessonPlans.selectStudents')}
                noResultsText={t('lessonPlans.noStudentsFound')}
                selectedCountText={t('lessonPlans.selectedCount')}
              />
            </div>

            {/* Date Range */}
            <div>
              <span className="text-sm font-label font-medium text-foreground-700 mb-2 block">{t('lessonPlans.dateRange')}</span>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <span className="text-xs text-foreground-500 mb-1 block">{t('lessonPlans.startDate')}</span>
                  <input
                    type="date"
                    value={dateStart}
                    onChange={(e) => setDateStart(e.target.value)}
                    className="w-full xl-input text-sm cursor-pointer"
                  />
                </div>
                <div>
                  <span className="text-xs text-foreground-500 mb-1 block">{t('lessonPlans.endDate')}</span>
                  <input
                    type="date"
                    value={dateEnd}
                    onChange={(e) => setDateEnd(e.target.value)}
                    className="w-full xl-input text-sm cursor-pointer"
                  />
                </div>
              </div>
            </div>

            {/* Theme / Unit Topic */}
            <div>
              <span className="text-sm font-label font-medium text-foreground-700 mb-2 block">{t('lessonPlans.themeTopic')}</span>
              <input
                type="text"
                value={theme}
                onChange={(e) => setTheme(e.target.value)}
                placeholder={t('lessonPlans.themePlaceholder')}
                className="w-full xl-input text-sm"
              />
              <p className="text-xs text-foreground-400 mt-1">{t('lessonPlans.themeHelper')}</p>
            </div>
          </div>
        </section>

        {/* ── Skill, Goal & Objective ── */}
        <section className="mb-8">
          <h3 className="font-heading font-700 text-lg text-foreground-900 mb-1">{t('lessonPlans.skillGoalSection')}</h3>

          <div className="bg-background-50 border border-background-200 rounded-xl p-5 space-y-5 mt-5">
            {/* Skill / Domain Checkboxes */}
            <div>
              <span className="text-sm font-label font-medium text-foreground-700 mb-2 block">{t('lessonPlans.skillDomain')}</span>
              <p className="text-xs text-foreground-500 mb-3">{t('lessonPlans.skillDomainDesc')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {skillDomains.map((sk) => {
                  const isSelected = selectedSkills.includes(sk.id);
                  return (
                    <button
                      key={sk.id}
                      type="button"
                      onClick={() => toggleSkill(sk.id)}
                      className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border text-left transition-all cursor-pointer ${
                        isSelected
                          ? 'border-accent-400 bg-accent-50'
                          : 'border-background-200 bg-background-50 hover:border-background-300'
                      }`}
                    >
                      <div className={`w-4 h-4 flex items-center justify-center rounded border-2 flex-shrink-0 mt-0.5 transition-colors ${
                        isSelected ? 'bg-accent-500 border-accent-500 text-background-50' : 'border-background-300'
                      }`}>
                        {isSelected && <i className="ri-check-line text-xs" />}
                      </div>
                      <span className="text-sm text-foreground-700 leading-snug">{sk.label}</span>
                    </button>
                  );
                })}
              </div>
              {selectedSkills.length === 0 && (
                <p className="text-xs text-foreground-400 mt-2">{t('lessonPlans.noSkillsSelected')}</p>
              )}
            </div>

            {/* Goals & Targets */}
            <div>
              <span className="text-sm font-label font-medium text-foreground-700 mb-2 block">{t('lessonPlans.goalsAndTargets')}</span>
              <div className="space-y-3">
                <div>
                  <span className="text-xs text-foreground-500 mb-1 block">{t('lessonPlans.longTermGoal')}</span>
                  <textarea
                    value={longTermGoal}
                    onChange={(e) => setLongTermGoal(e.target.value)}
                    placeholder={t('lessonPlans.longTermGoalPlaceholder')}
                    rows={3}
                    className="w-full xl-input text-sm resize-none"
                  />
                </div>
                <div>
                  <span className="text-xs text-foreground-500 mb-1 block">{t('lessonPlans.shortTermObjective')}</span>
                  <textarea
                    value={shortTermObjective}
                    onChange={(e) => setShortTermObjective(e.target.value)}
                    placeholder={t('lessonPlans.shortTermObjectivePlaceholder')}
                    rows={3}
                    className="w-full xl-input text-sm resize-none"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Learning Profiles & Resources ── */}
        <section className="mb-8">
          <h3 className="font-heading font-700 text-lg text-foreground-900 mb-1">{t('lessonPlans.learningProfiles')}</h3>

          <div className="bg-background-50 border border-background-200 rounded-xl p-5 space-y-5 mt-5">
            {/* Learning Styles */}
            <div>
              <span className="text-sm font-label font-medium text-foreground-700 mb-2 block">{t('lessonPlans.learningStylesTargeted')}</span>
              <p className="text-xs text-foreground-500 mb-3">{t('lessonPlans.learningStylesDesc')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {learningStyles.map((ls) => {
                  const isSelected = selectedStyles.includes(ls.id);
                  return (
                    <button
                      key={ls.id}
                      type="button"
                      onClick={() => toggleStyle(ls.id)}
                      className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border text-left transition-all cursor-pointer ${
                        isSelected
                          ? 'border-secondary-400 bg-secondary-50'
                          : 'border-background-200 bg-background-50 hover:border-background-300'
                      }`}
                    >
                      <div className={`w-4 h-4 flex items-center justify-center rounded border-2 flex-shrink-0 mt-0.5 transition-colors ${
                        isSelected ? 'bg-secondary-500 border-secondary-500 text-background-50' : 'border-background-300'
                      }`}>
                        {isSelected && <i className="ri-check-line text-xs" />}
                      </div>
                      <span className="text-sm text-foreground-700 leading-snug">{ls.label}</span>
                    </button>
                  );
                })}
              </div>
              {selectedStyles.length === 0 && (
                <p className="text-xs text-foreground-400 mt-2">{t('lessonPlans.noStylesSelected')}</p>
              )}
            </div>

            {/* TLM Materials */}
            <div>
              <span className="text-sm font-label font-medium text-foreground-700 mb-2 block">{t('lessonPlans.tlmMaterials')}</span>
              <p className="text-xs text-foreground-500 mb-3">{t('lessonPlans.tlmDesc')}</p>

              {/* Standard Materials */}
              <div className="mb-4">
                <span className="text-xs font-label font-medium text-foreground-600 mb-2 block">{t('lessonPlans.standardMaterials')}</span>
                <div className="flex flex-wrap gap-1.5">
                  {standardTLMMaterials.map((mat) => {
                    const isSelected = selectedMaterials.includes(mat);
                    return (
                      <button
                        key={mat}
                        type="button"
                        onClick={() => toggleMaterial(mat)}
                        className={`px-3 py-1.5 rounded-full text-xs font-label font-medium border transition-all cursor-pointer whitespace-nowrap ${
                          isSelected
                            ? 'bg-accent-100 border-accent-300 text-accent-800'
                            : 'bg-background-50 border-background-200 text-foreground-600 hover:border-background-300'
                        }`}
                      >
                        {isSelected && <i className="ri-check-line mr-1 text-xs" />}
                        {mat}
                      </button>
                    );
                  })}
                </div>
                {selectedMaterials.length === 0 && (
                  <p className="text-xs text-foreground-400 mt-2">{t('lessonPlans.noMaterialsSelected')}</p>
                )}
              </div>

              {/* Custom Materials */}
              <div>
                <span className="text-xs font-label font-medium text-foreground-600 mb-2 block">{t('lessonPlans.customMaterials')}</span>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {customMaterials.map((mat) => (
                    <span
                      key={mat}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-label font-medium bg-secondary-100 text-secondary-800 border border-secondary-200"
                    >
                      {mat}
                      <button
                        type="button"
                        onClick={() => removeCustomMaterial(mat)}
                        className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-secondary-300 transition-colors cursor-pointer"
                      >
                        <i className="ri-close-line text-xs" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customMaterialInput}
                    onChange={(e) => setCustomMaterialInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomMaterial(); } }}
                    placeholder={t('lessonPlans.addCustomMaterial')}
                    className="flex-1 xl-input text-sm"
                  />
                  <button
                    type="button"
                    onClick={addCustomMaterial}
                    className="px-3 py-2 bg-secondary-100 text-secondary-700 text-sm font-label font-medium rounded-lg hover:bg-secondary-200 transition-colors cursor-pointer"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Multi-Step Procedure ── */}
        <section className="mb-8">
          <div className="flex items-center justify-between mb-1">
            <div>
              <h3 className="font-heading font-700 text-lg text-foreground-900">{t('lessonPlans.procedureSection')}</h3>
              <p className="text-sm text-foreground-500">{t('lessonPlans.procedureDesc')}</p>
            </div>
            <button
              type="button"
              onClick={addStep}
              className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 text-sm font-label font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line" />
              {t('lessonPlans.addStep')}
            </button>
          </div>

          <div className="mt-4 space-y-3">
            {procedureSteps.length === 0 ? (
              <div className="bg-background-50 border border-dashed border-background-300 rounded-xl p-8 text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-list-check-2 text-foreground-400 text-xl" />
                </div>
                <p className="text-sm text-foreground-500">{t('lessonPlans.noProcedureSteps')}</p>
              </div>
            ) : (
              procedureSteps.map((step, idx) => (
                <ProcedureStepRow
                  key={step.id}
                  step={step}
                  index={idx}
                  onChange={updateStep}
                  onRemove={() => removeStep(step.id)}
                  stepNumberLabel={t('lessonPlans.stepNumber')}
                  titlePlaceholder={t('lessonPlans.stepTitlePlaceholder')}
                  educatorPlaceholder={t('lessonPlans.educatorActionPlaceholder')}
                  studentPlaceholder={t('lessonPlans.studentExpectationPlaceholder')}
                  promptLabel={t('lessonPlans.promptHierarchy')}
                  removeLabel={t('lessonPlans.removeStep')}
                />
              ))
            )}
          </div>
        </section>

        {/* ── Evaluation & Tracking ── */}
        <section className="mb-8">
          <div className="flex items-center justify-between mb-1">
            <div>
              <h3 className="font-heading font-700 text-lg text-foreground-900">{t('lessonPlans.evaluationSection')}</h3>
              <p className="text-sm text-foreground-500">{t('lessonPlans.evaluationDesc')}</p>
            </div>
            <button
              type="button"
              onClick={addMetric}
              className="flex items-center gap-2 px-4 py-2.5 bg-accent-500 text-background-50 text-sm font-label font-medium rounded-lg hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line" />
              {t('lessonPlans.addMetric')}
            </button>
          </div>

          <div className="mt-4 space-y-3">
            {evaluationMetrics.length === 0 ? (
              <div className="bg-background-50 border border-dashed border-background-300 rounded-xl p-8 text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-bar-chart-2-line text-foreground-400 text-xl" />
                </div>
                <p className="text-sm text-foreground-500">{t('lessonPlans.noEvaluationMetrics')}</p>
              </div>
            ) : (
              evaluationMetrics.map((metric, idx) => (
                <EvaluationRow
                  key={metric.id}
                  metric={metric}
                  index={idx}
                  onChange={updateMetric}
                  onRemove={() => removeMetric(metric.id)}
                  metricNumberLabel={t('lessonPlans.metricNumber')}
                  behaviorPlaceholder={t('lessonPlans.targetBehaviorPlaceholder')}
                  criteriaLabel={t('lessonPlans.measurementCriteria')}
                  thresholdPlaceholder={t('lessonPlans.successThresholdPlaceholder')}
                  removeLabel={t('lessonPlans.removeMetric')}
                />
              ))
            )}
          </div>
        </section>

        {/* ── Continuity & Final Review ── */}
        <section className="mb-8">
          <h3 className="font-heading font-700 text-lg text-foreground-900 mb-1">{t('lessonPlans.continuitySection')}</h3>

          <div className="bg-background-50 border border-background-200 rounded-xl p-5 space-y-4 mt-5">
            {/* Follow-Up Plan */}
            <div>
              <span className="text-sm font-label font-medium text-foreground-700 mb-1.5 block">{t('lessonPlans.followUpPlan')}</span>
              <textarea
                value={followUpPlan}
                onChange={(e) => setFollowUpPlan(e.target.value)}
                placeholder={t('lessonPlans.followUpPlanPlaceholder')}
                rows={4}
                className="w-full xl-input text-sm resize-none"
              />
            </div>

            {/* Final Evaluation */}
            <div>
              <span className="text-sm font-label font-medium text-foreground-700 mb-1.5 block">{t('lessonPlans.finalEvaluation')}</span>
              <p className="text-xs text-foreground-500 mb-2">{t('lessonPlans.finalEvaluationDesc')}</p>
              <textarea
                value={finalEvaluation}
                onChange={(e) => setFinalEvaluation(e.target.value)}
                placeholder={t('lessonPlans.finalEvaluationPlaceholder')}
                rows={4}
                className="w-full xl-input text-sm resize-none"
              />
            </div>
          </div>
        </section>

        {/* ── Actions ── */}
        <div className="flex items-center justify-between gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate('/dashboard/teacher-dashboard/lesson-plans')}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-label font-medium text-foreground-600 hover:text-foreground-800 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            {t('lessonPlans.backToDashboard')}
          </button>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleSaveDraft}
              className="px-5 py-2.5 border border-background-300 text-foreground-700 text-sm font-label font-medium rounded-lg hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('lessonPlans.saveDraft')}
            </button>
            <button
              type="button"
              onClick={() => { setTemplateNameInput(theme); setShowTemplateModal(true); }}
              className="flex items-center gap-1.5 px-5 py-2.5 border border-accent-300 text-accent-700 text-sm font-label font-medium rounded-lg hover:bg-accent-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-bookmark-line" />
              {t('lessonPlans.saveAsTemplate')}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-label font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('lessonPlans.submitPlan')}
            </button>
          </div>
        </div>
      </div>

      {/* Save as Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-foreground-950/50 backdrop-blur-sm" onClick={() => { setShowTemplateModal(false); setTemplateNameInput(''); }} />
          <div className="relative bg-background-50 rounded-xl shadow-floating p-6 w-full max-w-sm mx-4">
            <h4 className="font-heading font-700 text-foreground-900 mb-1">{t('lessonPlans.saveAsTemplateTitle')}</h4>
            <p className="text-xs text-foreground-500 mb-4">{t('lessonPlans.noTemplatesDesc')}</p>
            <div className="mb-5">
              <span className="text-xs font-label font-medium text-foreground-600 mb-1.5 block">{t('lessonPlans.templateName')}</span>
              <input
                type="text"
                value={templateNameInput}
                onChange={(e) => setTemplateNameInput(e.target.value)}
                placeholder={String(t('lessonPlans.templateNamePlaceholder'))}
                className="w-full xl-input text-sm"
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveAsTemplate(); }}
              />
            </div>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => { setShowTemplateModal(false); setTemplateNameInput(''); }}
                className="px-4 py-2 text-sm font-label font-medium text-foreground-600 hover:text-foreground-800 transition-colors cursor-pointer"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSaveAsTemplate}
                disabled={!templateNameInput.trim()}
                className="px-4 py-2 bg-accent-500 text-background-50 text-sm font-label font-medium rounded-lg hover:bg-accent-600 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}